import { SetStateAction, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import cn from "classnames";
import { produce } from "immer";

import { Chip } from "shared/components/Chip";
import { WizardData } from "shared/components/WizardNew/types";

import { SPLIT_TEST } from "shared/constants/split";
import { DEFAULT_MODE_WIZARD, SAFE_MODE_WIZARD } from "shared/constants/wizard";

import { WizardStore, WizardType } from "shared/context/WizardStoreContext";

import { simplyClick } from "shared/helpers/analytics";
import { isSplitTestRequired } from "shared/helpers/view";
import { setStepperState } from "shared/helpers/wizard";

import { useWizard, WizardStep } from "shared/hooks/useWizard";

import styles from "@platformAssets/components/Wizard.module.scss";

type Props = { wizardData: WizardData };
const MAX_SELECTION = 5;

const WizardOptions = ({ wizardData }: Props) => {
  const { pathname } = useLocation();
  const wizard = useWizard(wizardData);

  const step = pathname.replace("/", "") as WizardStep;

  const options: Record<string, string> | undefined = wizard[step]?.answers;
  const analytics = wizard[step]?.analytics;
  const isMultiple = wizard[step]?.multiple;

  const [wizardStore, setWizardStore] = useContext(WizardStore);

  const selectedValues = wizardStore.answers?.[step] || [];

  const { mode } = wizardData;
  const isSplitReducedWizardEnabled =
    mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(wizard.data, SPLIT_TEST.reducedWizard);

  if (!options) return null;

  useEffect(() => {
    if (mode === SAFE_MODE_WIZARD || isSplitReducedWizardEnabled) {
      setWizardStore(
        setStepperState(
          wizardStore,
          isSplitReducedWizardEnabled && step === WizardStep.Interests ? 2 : 1,
          mode,
          isSplitReducedWizardEnabled
        ) as unknown as SetStateAction<WizardType>
      );
    }
  }, [mode, step]);

  const handleClick = (option: string) => {
    const numericOption = Number(option);
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        const currentAnswers: number[] = wizard.answers[step] || [];

        if (isMultiple) {
          if (currentAnswers.includes(numericOption)) {
            wizard.answers[step] = currentAnswers.filter(
              (val) => val !== numericOption
            );
          } else if (currentAnswers.length < MAX_SELECTION) {
            wizard.answers[step] = [...currentAnswers, numericOption];
          }
        } else {
          wizard.answers[step] = [numericOption];
        }
      })
    );
    analytics?.choose?.forEach((item: string) => {
      simplyClick(item, null, false);
    });
  };

  return (
    <div className={styles.wizardOptionsWrapper}>
      {Object.entries(options).map(([value, text]) => {
        const numericValue = Number(value);
        const isSelected = selectedValues.includes(numericValue);
        const isDisabled =
          isMultiple && !isSelected && selectedValues.length >= MAX_SELECTION;
        return (
          <Chip
            customClass={cn(styles.wizardChip, {
              [styles.notMultiple]: !isMultiple,
            })}
            key={value}
            onClick={() => !isDisabled && handleClick(value)}
            isActive={isSelected}
            isDisabled={isDisabled}
            multiple={isMultiple}
          >
            {text as string}
          </Chip>
        );
      })}
    </div>
  );
};

export { WizardOptions };
